import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Box, Paper, Typography, CircularProgress, Alert, Button, Stack, List, ListItem, ListItemText, Divider } from '@mui/material';
import { API_URL } from '../utils/apiConfig';
import { AuthContext } from '../context/AuthContext';

export default function MySchedule() {
  const { user, isAuthenticated } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [shifts, setShifts] = useState([]);
  const [weekAnchor, setWeekAnchor] = useState(() => {
    const d = new Date();
    const day = d.getDay();
    const diff = (day === 0 ? -6 : 1) - day; // Monday
    d.setDate(d.getDate() + diff);
    d.setHours(0,0,0,0);
    return d;
  });

  const weekStartStr = useMemo(() => new Date(weekAnchor.getTime() - weekAnchor.getTimezoneOffset()*60000).toISOString().slice(0,10), [weekAnchor]);
  const weekEndStr = useMemo(() => {
    const s = new Date(weekAnchor);
    const e = new Date(s); e.setDate(s.getDate() + 6);
    return new Date(e.getTime() - e.getTimezoneOffset()*60000).toISOString().slice(0,10);
  }, [weekAnchor]);

  useEffect(() => {
    if (!user?.id) return;
    const fetchShifts = async () => {
      try {
        setLoading(true);
        setError('');
        const token = localStorage.getItem('token');
        const params = new URLSearchParams({ employeeId: String(user.id), start: weekStartStr, end: weekEndStr });
        const resp = await fetch(`${API_URL.replace(/\/$/, '')}/vnext/shifts?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await resp.json();
        if (!resp.ok) throw new Error(data.error || 'Failed to load shifts');
        setShifts(data.data || []);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    fetchShifts();
  }, [user?.id, weekStartStr, weekEndStr]);

  const parseLocal = (dt) => {
    if (!dt) return null;
    const [d, t] = String(dt).split(' ');
    if (!t) return new Date(dt);
    const [Y, M, D] = d.split('-').map(Number);
    const [h, m, s] = t.split(':').map(Number);
    return new Date(Y, (M - 1), D, h || 0, m || 0, s || 0, 0);
  };

  const byDay = useMemo(() => {
    const map = new Map();
    for (let i=0;i<7;i++) map.set(i, []);
    const start = new Date(weekAnchor);
    shifts.forEach(sh => {
      const s = parseLocal(sh.start_ts);
      const idx = Math.floor((s - start) / 86400000);
      if (idx >= 0 && idx < 7) map.get(idx).push(sh);
    });
    // Sort by start time within each day
    for (const [k, arr] of map.entries()) {
      arr.sort((a,b) => parseLocal(a.start_ts) - parseLocal(b.start_ts));
    }
    return map;
  }, [shifts, weekAnchor]);

  if (!isAuthenticated) return null;
  if (loading) return <Box display="flex" justifyContent="center" mt={4}><CircularProgress/></Box>;
  if (error) return <Alert severity="error" sx={{ mt:2 }}>{error}</Alert>;

  const dayLabel = (offset) => {
    const d = new Date(weekAnchor); d.setDate(d.getDate() + offset);
    return d.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });
  };

  return (
    <Paper sx={{ p:2 }}>
      <Stack direction={{ xs:'column', sm:'row' }} spacing={1} alignItems={{ sm:'center' }} justifyContent="space-between" sx={{ mb:2 }}>
        <Typography variant="h6">My Schedule</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button variant="outlined" size="small" onClick={() => setWeekAnchor(prev => { const d = new Date(prev); d.setDate(d.getDate()-7); return d; })}>◀ Prev</Button>
          <Typography variant="body2" sx={{ minWidth: 160, textAlign: 'center' }}>{`${weekStartStr} → ${weekEndStr}`}</Typography>
          <Button variant="outlined" size="small" onClick={() => setWeekAnchor(prev => { const d = new Date(prev); d.setDate(d.getDate()+7); return d; })}>Next ▶</Button>
          <Button variant="text" size="small" onClick={() => setWeekAnchor(() => { const d = new Date(); const day = d.getDay(); const diff = (day===0?-6:1)-day; d.setDate(d.getDate()+diff); d.setHours(0,0,0,0); return d; })}>Today</Button>
        </Stack>
      </Stack>

      <List>
        {Array.from({ length: 7 }, (_, i) => i).map((i) => (
          <Box key={i}>
            <Typography variant="subtitle2" sx={{ mt: i===0?0:2, color: 'text.secondary' }}>{dayLabel(i)}</Typography>
            {byDay.get(i)?.length ? (
              byDay.get(i).map((sh) => {
                const s = parseLocal(sh.start_ts);
                const e = parseLocal(sh.end_ts);
                const primary = `${String(s.getHours()).padStart(2,'0')}:${String(s.getMinutes()).padStart(2,'0')} - ${String(e.getHours()).padStart(2,'0')}:${String(e.getMinutes()).padStart(2,'0')}`;
                const secondary = `${sh.role || 'Shift'} • ${sh.status || 'scheduled'}`;
                return (
                  <ListItem key={sh.id} dense sx={{ py:0.5 }}>
                    <ListItemText primary={primary} secondary={secondary} />
                  </ListItem>
                );
              })
            ) : (
              <Typography variant="body2" color="text.disabled">No shifts</Typography>
            )}
            {i < 6 && <Divider sx={{ mt:1 }}/>}            
          </Box>
        ))}
      </List>
    </Paper>
  );
}

